import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { FaCalendarAlt, FaUser, FaArrowLeft, FaNewspaper, FaExclamationCircle } from "react-icons/fa";
import { apiCall } from "../config/postgres";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { createPlaceholderDataUrl, handleImageError } from "../utils/imageUtils";

const PostDetailPage = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const isRTL = i18n.language === "fa";

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);

    const fetchPost = async () => {
      try {
        setIsLoading(true);
        const data = await apiCall(`/api/posts/${id}`);
        setPost(data);
        setError(null);
      } catch (error) {
        console.error("Error fetching post:", error);
        setError(t('posts.errorLoading', 'Failed to load post'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchPost();
  }, [id, t]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="fixed top-0 left-0 right-0 h-16 bg-safir-dark z-40"></div>
        <div className="relative z-50">
          <Navbar />
        </div>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-safir-primary mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">{t('common.loading')}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="fixed top-0 left-0 right-0 h-16 bg-safir-dark z-40"></div>
        <div className="relative z-50">
          <Navbar />
        </div>
        <div className="container mx-auto px-4 py-16">
          <motion.div
            className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-8 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <FaExclamationCircle className="mx-auto mb-4 text-6xl text-red-400" />
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {error || t('posts.postNotFound', 'Post not found')}
            </h2>
            <p className="text-gray-600 mb-6">
              {t('posts.postNotFoundDesc', 'The post you are looking for may have been removed or does not exist.')}
            </p>
            <Link
              to="/news"
              className="inline-flex items-center px-6 py-3 bg-safir-primary hover:bg-safir-secondary text-white rounded-lg transition-colors duration-300"
            >
              <FaArrowLeft className={`mr-2 ${isRTL ? 'rotate-180' : ''}`} />
              {t('posts.backToNews', 'Back to News')}
            </Link>
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  const title = isRTL ? post.title_fa : post.title_en;
  const content = isRTL ? post.content_fa : post.content_en;
  const authorName = isRTL ? post.author_name_fa : post.author_name_en;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Dark background behind navbar */}
      <div className="fixed top-0 left-0 right-0 h-16 bg-safir-dark z-40"></div>
      <div className="relative z-50">
        <Navbar />
      </div>

      {/* Header with Back Button */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <motion.button
            onClick={() => navigate('/news')}
            className="inline-flex items-center text-safir-primary hover:text-safir-secondary font-semibold transition-colors duration-300"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <FaArrowLeft className={`mr-2 ${isRTL ? 'rotate-180' : ''}`} />
            {t('posts.backToNews', 'Back to News')}
          </motion.button>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <motion.article
          className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Post Image */}
          {post.post_image && (
            <div className="relative h-64 md:h-96 overflow-hidden">
              <img
                src={post.post_image}
                alt={title}
                className="w-full h-full object-cover"
                onError={(e) => handleImageError(e, 800, 400, title)}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            </div>
          )}

          {/* Post Content */}
          <div className="p-6 md:p-10">
            {/* Title */}
            <motion.h1
              className="text-3xl md:text-4xl font-bold text-safir-dark mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              dir={isRTL ? "rtl" : "ltr"}
            >
              {title}
            </motion.h1>

            {/* Author and Date */}
            <motion.div
              className="flex flex-wrap items-center gap-4 mb-8 pb-6 border-b border-gray-200"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {/* Author */}
              <div className="flex items-center">
                <img
                  src={post.author_image || createPlaceholderDataUrl(50, 50, authorName?.charAt(0) || "A")}
                  alt={authorName}
                  className="w-12 h-12 rounded-full object-cover mr-3 border-2 border-safir-primary"
                  onError={(e) => handleImageError(e, 50, 50, authorName?.charAt(0) || "A")}
                />
                <div>
                  <p className="text-sm text-gray-500">{t('posts.writtenBy', 'Written by')}</p>
                  <p className="font-semibold text-safir-dark">
                    {authorName || t('posts.defaultAuthor', 'Admin')}
                  </p>
                </div>
              </div>

              {/* Date */}
              <div className="flex items-center text-gray-600">
                <div className="w-px h-8 bg-gray-300 mx-4"></div>
                <FaCalendarAlt className="mr-2 text-safir-primary" />
                <span>
                  {new Date(post.created_at).toLocaleDateString(isRTL ? 'fa-IR' : 'en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
            </motion.div>

            {/* Full Content */}
            <motion.div
              className="prose prose-lg max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              dir={isRTL ? "rtl" : "ltr"}
            >
              {content}
            </motion.div>

            {/* Updated Date (if different from created) */}
            {post.updated_at && post.updated_at !== post.created_at && (
              <motion.p
                className="text-sm text-gray-500 mt-8 pt-4 border-t border-gray-200"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                {t('posts.lastUpdated', 'Last updated')}: {new Date(post.updated_at).toLocaleDateString(isRTL ? 'fa-IR' : 'en-US')}
              </motion.p>
            )}
          </div>
        </motion.article>

        {/* Related Posts Section - Could be added here in the future */}
      </div>

      <Footer />
    </div>
  );
};

export default PostDetailPage;
